class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector(".f-section-header");
  }

  connectedCallback() {
    this.classes = {
      active: "f-header__mega-active",
      headerScheme: this.dataset.headerColorScheme,
      dropdownScheme: this.dataset.dropdownColorScheme,
    };

    this.grandLinks = this.querySelectorAll(".f-site-nav__sub-item--has-child");
    this.grandLinks &&
      this.grandLinks.forEach((item) => {
        this.handleGrandLinksPosition(item);
      });
  }

  handleMegaItemActive(dropdown) {
    // Calculate backdrop height.
    const rect = dropdown.getBoundingClientRect();
    this.style.setProperty(
      "--f-dropdown-height",
      Math.ceil(rect.height) + "px"
    );

    this.classList.remove(this.classes.headerScheme);
    this.classList.add(this.classes.active, this.classes.dropdownScheme);
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.clientHeight + "px"
    );
  }

  handleMegaItemDeactive() {
    this.classList.remove(this.classes.active, this.classes.dropdownScheme);
    this.classList.add(this.classes.headerScheme);
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.clientHeight + "px"
    );
  }

  handleGrandLinksPosition(target) {
    const dropdownLV3 = target.querySelector(".f-site-nav__dropdown");
    if (dropdownLV3) {
      const rect = dropdownLV3.getBoundingClientRect();
      dropdownLV3.classList.remove("f-site-nav__dropdown-reversed");
      if (
        (!FoxThemeSettings.isRTL &&
          document.documentElement.clientWidth < rect.x + rect.width + 10) ||
        (FoxThemeSettings.isRTL && rect.x < 10)
      ) {
        dropdownLV3.classList.add("f-site-nav__dropdown-reversed");
      }
    }
  }
}
customElements.define("basic-header", BasicHeader, { extends: "header" });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    this.stickyClasses = {
      pinned: "header-pinned",
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.headerSection.classList.add("header-sticky");
    this.stickyType = this.dataset.stickyType;
    this.currentScrollTop = 0;

    this.headerBounds = this.headerSection
      .querySelector(".header")
      .getBoundingClientRect();

    this.onScrollHandler = this._onScroll.bind(this);
    this._onScroll();
    window.addEventListener("scroll", this.onScrollHandler, false);
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScrollHandler);
  }

  get isAlwaysSticky() {
    return this.stickyType === "always";
  }

  _onScroll() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    // Avoid layout thrashing by caching values
    const headerBoundsTop = this.offsetTop + this.headerBounds.height;

    const headerBoundsBottom =
      this.headerBounds.top + this.headerBounds.height + 100;

    // Utilize batch CSS classes and updates
    requestAnimationFrame(() => {
      if (scrollTop > headerBoundsTop) {
        headerSection.classList.add("header-scrolled");

        if (this.isAlwaysSticky) {
          document.body.classList.add(this.stickyClasses.pinned);
        } else {
          // Sticky on scroll up.
          if (
            scrollTop < this.currentScrollTop ||
            scrollTop < headerBoundsBottom
          ) {
            document.body.classList.add(this.stickyClasses.pinned);
          } else {
            document.body.classList.remove(this.stickyClasses.pinned);
          }
        }
      } else {
        headerSection.classList.remove("header-scrolled");
        document.body.classList.remove(this.stickyClasses.pinned);
      }

      this.currentScrollTop = scrollTop;
    });
  }

  _closeMenuDisclosure() {
    this.disclosures =
      this.disclosures || this.header.querySelectorAll("header-menu");
    this.disclosures.forEach((disclosure) => disclosure.close());

    this.siteNav = this.siteNav || this.header.querySelector("site-nav");
    this.siteNav && this.siteNav.closeMegaDropdowns();
  }
}
customElements.define("sticky-header", StickyHeader, { extends: "header" });

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest(".site-header");
    this.classes = {
      itemActive: "f-menu__item-active",
    };
    this.header.timeoutEnter = null;
    this.header.timeoutLeave = null;
  }

  onToggle(evt) {
    const { target } = evt;
    const li = target.closest(".f-site-nav__item");
    const isOpen = this.mainDetailsToggle.open;
    const isMega = li.classList.contains("f-site-nav__item--mega");

    if (!isOpen) {
      clearTimeout(this.header.timeoutEnter);
      li.classList.remove(this.classes.itemActive);
      this.header.timeoutLeave = setTimeout(() => {
        this.header.handleMegaItemDeactive();
      }, 160);
    } else {
      if (isMega) {
        clearTimeout(this.header.timeoutLeave);
        const dropdown = li.querySelector(".f-site-nav__dropdown");
        this.header.handleMegaItemActive(dropdown);
        this.header.timeoutEnter = setTimeout(() => {
          li.classList.add(this.classes.itemActive);
        }, 160);
      } else {
        li.classList.add(this.classes.itemActive);
      }
    }
  }
}
customElements.define("header-menu", HeaderMenu);

class SiteNav extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest(".site-header");
    this.classes = {
      itemActive: "f-menu__item-active",
    };

    this.megaItems = this.querySelectorAll(".f-site-nav__item--mega");
    this.timeoutEnter = null;
    this.timeoutLeave = null;
    this.isHover =
      this.header &&
      this.header.classList.contains("show-dropdown-menu-on-hover");
    if (this.isHover) {
      this.megaItems &&
        this.megaItems.forEach((megaItem, index) => {
          megaItem.addEventListener("mouseenter", (evt) =>
            this.onMenuItemEnter(evt, index)
          );
          megaItem.addEventListener("mouseleave", (evt) =>
            this.onMenuItemLeave(evt, index)
          );
        });
      this.tpInitClickToggles();
    }
  }

  // tom&pat 9.9 (Theo, 08.10.2026, Rundgang Aufgaben Z. 27 d): Im Hover-Modus oeffnet ein Klick oder Enter auf einen
  // Menuepunkt mit Untermenue ("Produkte") das Untermenue, statt zur Linkadresse zu springen. Markierung kommt aus
  // snippets/site-nav.liquid (data-tp-nav-toggle + aria-expanded/aria-controls). Zustand: Klasse .tp-nav-offen am
  // <li> (CSS in assets/pat-custom.css, Block 9.9) plus die Hersteller-Klasse f-menu__item-active (Mega-Hintergrund).
  // Maus: Klick haelt offen (Hover hat meist schon geoeffnet); Tastatur (Enter/Leertaste = click mit detail 0) schaltet um.
  // Schliessen: Escape, Klick ausserhalb, Fokus verlaesst den Menuepunkt, Maus verlaesst den Menuepunkt.
  tpInitClickToggles() {
    this.tpToggles = this.querySelectorAll("[data-tp-nav-toggle]");
    if (!this.tpToggles.length) return;
    // tom&pat 10.5 (Sam, 11.10.2026, Shop Walk Nr. 77, Patrick): Ein Mausklick auf "Produkte" fuehrt direkt zur
    // Linkadresse (Alle Produkte); das Untermenue oeffnet weiter beim Ueberfahren. Enter folgt ebenfalls dem Link.
    // Untermenue per Tastatur: Leertaste oder Pfeil nach unten. Touch: erster Tipp oeffnet, zweiter Tipp folgt dem Link.
    this.tpToggles.forEach((toggle) => {
      toggle.addEventListener("pointerdown", (evt) => {
        this.tpLetzterZeiger = evt.pointerType || "mouse";
      });
      toggle.addEventListener("click", (evt) => {
        const li = toggle.closest(".f-site-nav__item");
        if (!li) return;
        const offen = li.classList.contains("tp-nav-offen");
        const zeiger = this.tpLetzterZeiger;
        this.tpLetzterZeiger = null;
        if (evt.detail !== 0 && zeiger === "touch" && !offen) {
          evt.preventDefault();
          this.tpOpen(li);
        }
      });
      toggle.addEventListener("keydown", (evt) => {
        if (evt.key !== " " && evt.key !== "Spacebar" && evt.key !== "ArrowDown") return;
        const li = toggle.closest(".f-site-nav__item");
        if (!li) return;
        evt.preventDefault();
        if (li.classList.contains("tp-nav-offen") && evt.key !== "ArrowDown") {
          this.tpClose(li);
        } else {
          this.tpOpen(li);
          if (evt.key === "ArrowDown") {
            const erster = li.querySelector(".f-site-nav__dropdown a");
            erster && erster.focus();
          }
        }
      });
      const li = toggle.closest(".f-site-nav__item");
      if (!li) return;
      li.addEventListener("keydown", (evt) => {
        if (evt.key === "Escape" && li.classList.contains("tp-nav-offen")) {
          evt.preventDefault();
          this.tpClose(li);
          toggle.focus();
        }
      });
      li.addEventListener("focusout", (evt) => {
        if (evt.relatedTarget && li.contains(evt.relatedTarget)) return;
        this.tpClose(li);
      });
      li.addEventListener("mouseleave", () => this.tpClose(li, true));
    });
    document.addEventListener("click", (evt) => {
      if (this.contains(evt.target)) return;
      this.tpToggles.forEach((toggle) => {
        const li = toggle.closest(".f-site-nav__item");
        li && this.tpClose(li);
      });
    });
  }

  tpSetExpanded(li, offen) {
    const toggle = li.querySelector("[data-tp-nav-toggle]");
    toggle && toggle.setAttribute("aria-expanded", offen ? "true" : "false");
  }

  tpOpen(li) {
    clearTimeout(this.timeoutLeave);
    this.tpToggles &&
      this.tpToggles.forEach((toggle) => {
        const other = toggle.closest(".f-site-nav__item");
        if (other && other !== li) this.tpClose(other, true);
      });
    const dropdown = li.querySelector(".f-site-nav__dropdown");
    if (dropdown && li.classList.contains("f-site-nav__item--mega")) {
      this.header.handleMegaItemActive(dropdown);
    }
    li.classList.add("tp-nav-offen", this.classes.itemActive);
    this.tpSetExpanded(li, true);
  }

  tpClose(li, nurZustand) {
    if (!li.classList.contains("tp-nav-offen")) {
      if (!nurZustand) this.tpSetExpanded(li, false);
      return;
    }
    li.classList.remove("tp-nav-offen", this.classes.itemActive);
    this.tpSetExpanded(li, false);
    if (li.classList.contains("f-site-nav__item--mega")) {
      this.header.handleMegaItemDeactive();
    }
  }

  onMenuItemEnter(evt) {
    clearTimeout(this.timeoutLeave);

    const { target } = evt;

    /**
     * If hover from prev item to sibling items then need to close prev sub menu immediately.
     */
    if (!target.classList.contains(this.classes.itemActive)) {
      this.megaItems &&
        this.megaItems.forEach((megaItem) => {
          megaItem.classList.remove(this.classes.itemActive);
        });
    }

    const dropdown = target.querySelector(".f-site-nav__dropdown");

    if (dropdown) {
      this.header.handleMegaItemActive(dropdown);

      this.timeoutEnter = setTimeout(() => {
        target.classList.add(this.classes.itemActive);
      });
      this.tpSetExpanded && this.tpSetExpanded(target, true);
    }
  }

  onMenuItemLeave(evt) {
    const { target } = evt;

    clearTimeout(this.timeoutEnter);
    
    this.header.handleMegaItemDeactive();

    this.timeoutLeave = setTimeout(() => {
      target.classList.remove(this.classes.itemActive);
    });
    this.tpSetExpanded && this.tpSetExpanded(target, false);
  }

  closeMegaDropdowns() {
    clearTimeout(this.timeoutEnter);
    this.megaItems &&
      this.megaItems.forEach((megaItem) => {
        megaItem.classList.remove(this.classes.itemActive);
      });
    this.timeoutLeave = setTimeout(() => {
      this.header.handleMegaItemDeactive();
    });
  }

  disconnectedCallback() {
    this.megaItems &&
      this.megaItems.forEach((megaItem, index) => {
        megaItem.removeEventListener("mouseenter", (evt) =>
          this.onMenuItemEnter(evt, index)
        );
        megaItem.removeEventListener("mouseleave", (evt) =>
          this.onMenuItemLeave(evt, index)
        );
      });
  }
}
customElements.define("site-nav", SiteNav);